import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useFormik } from 'formik'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateWatchlist } from '@pages/Trade/Markets/Instruments/hooks/useUpdateWatchlist'

import { BaseModalType } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { Input } from '@ui/components/Input/Input'
import { Modal } from '@ui/components/Modal'
import { CloseIcon } from '@ui/icons/Close.icon'
import { SearchIcon } from '@ui/icons/Search.icon'
import { TableNoResults } from '@ui/v2/Table/Table'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'

import { Box } from '@shared/components/Box'

import { InstrumentSelectRow } from './components/InstrumentSelectRow'
import { InstrumentSkeletonRows } from './components/InstrumentSkeletonRows'
import { useWatchlistNameValidation } from './hooks/useWatchlistNameValidation'

interface EditWatchlistModalProps {
  watchlistName: string
  watchlistId?: number
  accountCode?: string
  existingSymbols?: string[]
  existingNames?: string[]
  nameDisabled?: boolean
  onSave?: (name: string, symbols: string[]) => void
  onEdited?: (newName: string) => void
}

interface FormValues {
  name: string
}

export const EditWatchlistModal: BaseModalType<EditWatchlistModalProps> = ({
  close,
  watchlistName: initialName,
  watchlistId,
  accountCode,
  existingSymbols = [],
  existingNames = [],
  nameDisabled = false,
  onEdited,
}) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { mutate: updateWatchlist, isPending } = useUpdateWatchlist()
  const validateName = useWatchlistNameValidation(existingNames, initialName)

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set(existingSymbols))

  const { values, errors, setFieldValue, handleSubmit } = useFormik<FormValues>({
    initialValues: { name: initialName },
    validate: validateName,
    onSubmit: () => {
      if (watchlistId !== undefined) {
        const trimmedName = values.name.trim()
        const nameChanged = trimmedName !== initialName.trim()
        updateWatchlist(
          {
            id: watchlistId,
            payload: { name: nameChanged ? trimmedName : undefined, symbols: Array.from(selected) },
          },
          {
            onSuccess: () => {
              onEdited?.(trimmedName)
              close()
            },
          }
        )
      }
    },
  })

  const showClearIcon = search.length > 0 ? CloseIcon : undefined
  const debouncedSearch = useDebouncedValue(search.trim(), 400)

  const queryParams = useMemo(
    () => ({
      symbols: debouncedSearch ? `*${debouncedSearch}*` : undefined,
      symbolDescription: debouncedSearch || undefined,
    }),
    [debouncedSearch]
  )

  const { data: instruments = [], isLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.instrumentsModal(accountCode, null, debouncedSearch, ''),
    queryFn: () => apiClient.getDxtradeAccountInstruments(accountCode!, queryParams),
    enabled: !!accountCode,
  })

  const initialSelectedSet = useMemo(() => new Set(existingSymbols), [existingSymbols])

  const sortedInstruments = useMemo(() => {
    if (!instruments.length) return instruments
    return [...instruments].sort((a, b) => {
      const aSelected = initialSelectedSet.has(a.symbol ?? '')
      const bSelected = initialSelectedSet.has(b.symbol ?? '')
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }, [instruments, initialSelectedSet])

  const toggleInstrument = (symbol: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(symbol)) {
        next.delete(symbol)
      } else {
        next.add(symbol)
      }
      return next
    })
  }
  const isSubmitDisabled = isPending || !values.name.trim() || !!errors.name
  return (
    <Modal>
      <Modal.Header>{t('Instruments.Edit watchlist')}</Modal.Header>
      <Modal.Body>
        <Input
          label={t('Instruments.Watchlist name')}
          fullWidth
          disabled={nameDisabled}
          value={values.name}
          error={!!errors.name}
          hint={errors.name}
          onChange={(e) => {
            setFieldValue('name', e.target.value)
          }}
        />
        <Input
          label=''
          fullWidth
          placeholder={t('Instruments.Search instruments')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant='search'
          size='lg'
          leftIcon={SearchIcon}
          rightIcon={showClearIcon}
          onEndIconClick={() => setSearch('')}
        />
        <Box flex direction='column' fullWidth minHeight={390}>
          {isLoading ? (
            <InstrumentSkeletonRows />
          ) : instruments.length === 0 ? (
            <TableNoResults
              title={t('Instruments.No results found')}
              content={
                <Body1 color='text-med-em'>{t('Instruments.Try adjusting your search')}</Body1>
              }
            />
          ) : (
            sortedInstruments.map((instrument) => {
              const symbol = instrument.symbol ?? ''
              return (
                <InstrumentSelectRow
                  key={symbol}
                  symbol={symbol}
                  symbolDescription={instrument.symbolDescription}
                  selected={selected.has(symbol)}
                  onToggle={toggleInstrument}
                />
              )
            })
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={() => handleSubmit()}
          disabled={isSubmitDisabled}
          loading={isPending}
        >
          {t('Instruments.Save')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
